import React, { useState } from "react";
import { Loader2, Sparkles, RefreshCw } from "lucide-react";
import { api, errMessage } from "../../lib/api";
import { useLang } from "../../lib/LangContext";
import { Container, SectionHeading } from "./Ui";

const FIELDS = [
  { key: "business_type", label: "Business type", placeholder: "Restaurant" },
  { key: "business_name", label: "Business name", placeholder: "Bella Rotterdam" },
  { key: "promotion", label: "What do you want to promote?", placeholder: "New summer menu" },
];

const BLOCKS = [
  { key: "instagram_caption", title: "Instagram caption" },
  { key: "facebook_caption", title: "Facebook caption" },
  { key: "story_concept", title: "Story concept" },
  { key: "reel_concept", title: "Reel concept" },
];

export default function LiveDemo() {
  const { tr, lang } = useLang();
  const [form, setForm] = useState({ business_type: "", business_name: "", promotion: "" });
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const generate = async () => {
    setError("");
    if (!form.business_type.trim() || !form.business_name.trim() || !form.promotion.trim()) {
      setError(tr("Please fill in all three fields so we can create a useful example."));
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/demo/generate", { ...form, language: lang });
      setResult(data);
    } catch (e) {
      setError(errMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="border-y border-slate-200 bg-white py-20 sm:py-24" data-testid="home-live-demo">
      <Container>
        <SectionHeading
          eyebrow="Try it now"
          title="See what PostMint can create."
          body="Tell us three things about your business and watch PostMint write a real post for it."
        />

        <div className="mt-10 grid gap-6 lg:grid-cols-[0.85fr_1.15fr]">
          <div className="rounded-3xl border border-slate-200 bg-[#FAFAFA] p-6 sm:p-7">
            <div className="space-y-4">
              {FIELDS.map((f) => (
                <label key={f.key} className="block">
                  <span className="text-sm font-semibold text-slate-800">{tr(f.label)}</span>
                  <input
                    value={form[f.key]}
                    onChange={set(f.key)}
                    placeholder={f.placeholder}
                    data-testid={`demo-input-${f.key.replace(/_/g, "-")}`}
                    className="mt-2 w-full rounded-2xl border border-slate-300 bg-white px-4 py-3 text-base text-slate-900 outline-none transition-colors placeholder:text-slate-400 focus:border-[#F95738]"
                  />
                </label>
              ))}
            </div>

            <button
              type="button"
              onClick={generate}
              disabled={busy}
              data-testid="demo-generate-button"
              className="mt-6 inline-flex w-full items-center justify-center rounded-full bg-[#F95738] px-6 py-3.5 text-base font-semibold text-white shadow-[0_10px_30px_rgba(249,87,56,0.3)] transition-all duration-200 hover:-translate-y-0.5 hover:bg-[#E0482B] disabled:translate-y-0 disabled:opacity-60"
            >
              {busy ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
                  {tr("Writing your example…")}
                </>
              ) : (
                <>
                  <Sparkles className="mr-2 h-4 w-4" aria-hidden="true" />
                  {tr("Generate Example")}
                </>
              )}
            </button>

            {error && (
              <p className="mt-4 text-sm font-medium text-[#B3341A]" data-testid="demo-error">
                {error}
              </p>
            )}
            <p className="mt-4 text-xs leading-relaxed text-slate-500">
              {tr("Examples are generated for demonstration. Always check facts, prices and dates before publishing.")}
            </p>
          </div>

          <div
            className="rounded-3xl border border-slate-200 bg-white p-6 soft-shadow sm:p-7"
            data-testid="demo-output-panel"
          >
            {!result && !busy && (
              <div className="flex h-full min-h-[18rem] flex-col justify-center">
                <p className="font-display text-xl font-semibold text-slate-900">
                  {tr("Your example appears here.")}
                </p>
                <p className="mt-3 max-w-md text-sm leading-relaxed text-slate-600">
                  {tr("PostMint writes an Instagram caption, a Facebook version, a story and a Reel concept — the same way it does for paying customers every month.")}
                </p>
              </div>
            )}

            {busy && !result && (
              <div className="space-y-3" aria-hidden="true">
                {[0, 1, 2, 3, 4, 5].map((i) => (
                  <div
                    key={i}
                    className="h-4 animate-pulse rounded-full bg-slate-100"
                    style={{ width: `${90 - i * 9}%`, animationDelay: `${i * 90}ms` }}
                  />
                ))}
              </div>
            )}

            {result && (
              <div className="space-y-5">
                <span className="inline-flex rounded-full bg-slate-900 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-white">
                  {tr("Example")}
                </span>
                {BLOCKS.map((b) =>
                  result[b.key] ? (
                    <div key={b.key} data-testid={`demo-output-${b.key.replace(/_/g, "-")}`}>
                      <h3 className="font-display text-base font-semibold text-slate-900">{tr(b.title)}</h3>
                      <p className="mt-2 whitespace-pre-line text-sm leading-relaxed text-slate-600">
                        {result[b.key]}
                      </p>
                    </div>
                  ) : null
                )}
                {result.visual_idea && (
                  <div className="rounded-2xl bg-[#FAFAFA] p-4">
                    <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">
                      {tr("Visual idea")}
                    </p>
                    <p className="mt-1.5 text-sm text-slate-700">{result.visual_idea}</p>
                  </div>
                )}
                <div className="flex flex-wrap items-center gap-3">
                  {result.cta && (
                    <span className="rounded-full bg-[#F95738] px-4 py-2 text-sm font-semibold text-white">
                      {result.cta}
                    </span>
                  )}
                  {result.hashtags && <span className="text-xs text-slate-400">{result.hashtags}</span>}
                </div>
                {Array.isArray(result.verify) && result.verify.length > 0 && (
                  <div
                    className="rounded-2xl border border-[#FFC43D]/50 bg-[#FFFBEF] p-4"
                    data-testid="demo-verify-notice"
                  >
                    <p className="text-sm font-semibold text-slate-900">{tr("Please verify before publishing")}</p>
                    <ul className="mt-2 list-inside list-disc space-y-1 text-sm text-slate-600">
                      {result.verify.map((v) => (
                        <li key={v}>{v}</li>
                      ))}
                    </ul>
                  </div>
                )}
                <button
                  type="button"
                  onClick={generate}
                  disabled={busy}
                  data-testid="demo-regenerate-button"
                  className="inline-flex items-center gap-2 rounded-full border border-slate-300 px-5 py-2.5 text-sm font-semibold text-slate-800 transition-colors hover:border-slate-400 disabled:opacity-60"
                >
                  <RefreshCw className={`h-4 w-4 ${busy ? "animate-spin" : ""}`} aria-hidden="true" />
                  {tr("Create another version")}
                </button>
              </div>
            )}
          </div>
        </div>
      </Container>
    </section>
  );
}
